import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/api';
import { Card } from '../components/ui/card';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  BarChart, PieChart, Pie, Cell,
} from 'recharts';
import {
  ClipboardText, Calendar, TrendUp, TrendDown, CurrencyEur, Users, Crown, ChartLine, CircleNotch,
  DownloadSimple, Target, Warning, MapPin, Briefcase, Timer,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const COLORS = ['#1565C0', '#2CA9E1', '#7DD3FC', '#0D3B66', '#38BDF8', '#93C5FD', '#1E40AF', '#60A5FA'];

const Delta = ({ d }) => {
  if (!d) return null;
  const up = d.pct >= 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${up ? 'text-emerald-600' : 'text-red-600'}`}>
      {up ? <TrendUp size={14} /> : <TrendDown size={14} />}{Math.abs(d.pct)}%
    </span>
  );
};

const Kpi = ({ icon: Icon, label, value, sub, delta, testid }) => (
  <Card className="rounded-2xl p-5" data-testid={testid}>
    <div className="flex items-center gap-2 mb-2">
      <Icon size={20} className="text-primary" weight="duotone" />
      <p className="text-xs text-foreground/60 uppercase tracking-wider">{label}</p>
    </div>
    <div className="flex items-end gap-2">
      <p className="text-3xl font-heading font-bold text-foreground">{value}</p>
      <Delta d={delta} />
    </div>
    {sub && <p className="text-xs text-foreground/60 mt-1">{sub}</p>}
  </Card>
);

const toArr = (obj) => Object.entries(obj || {}).map(([name, count]) => ({ name, count }));

const AnalyticsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weeks, setWeeks] = useState(12);
  const [location, setLocation] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [goals, setGoals] = useState({ target_requests_month: 0, target_bookings_month: 0 });
  const [savingGoals, setSavingGoals] = useState(false);

  const fetchData = useCallback(async (w, loc, svc, s, e) => {
    setLoading(true);
    try {
      const r = await api.get(`/api/admin/crm/analytics?weeks=${w}&location=${encodeURIComponent(loc || '')}&service_type=${encodeURIComponent(svc || '')}&start=${s || ''}&end=${e || ''}`);
      setData(r.data);
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(weeks, location, serviceType, start, end); }, [weeks, location, serviceType, start, end, fetchData]);
  useEffect(() => { api.get('/api/admin/settings/goals').then((r) => setGoals(r.data)).catch(() => {}); }, []);

  const exportReport = async (format) => {
    try {
      const r = await api.get(`/api/admin/crm/analytics/export?weeks=${weeks}&location=${encodeURIComponent(location || '')}&service_type=${encodeURIComponent(serviceType || '')}&start=${start || ''}&end=${end || ''}&format=${format}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `analytics.${format}`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch { /* silent */ }
  };

  const saveGoals = async () => {
    setSavingGoals(true);
    try {
      await api.put('/api/admin/settings/goals', {
        target_requests_month: Number(goals.target_requests_month) || 0,
        target_bookings_month: Number(goals.target_bookings_month) || 0,
      });
      fetchData(weeks, location, serviceType, start, end);
    } catch { /* silent */ } finally { setSavingGoals(false); }
  };

  if (loading && !data) {
    return <div className="flex items-center gap-2 text-slate-500 py-20 justify-center"><CircleNotch size={18} className="animate-spin" /> Caricamento analytics…</div>;
  }
  if (!data) return null;

  const t = data.totals;
  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 space-y-6" data-testid="analytics-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-bold text-2xl text-foreground flex items-center gap-2"><ChartLine size={24} weight="duotone" className="text-primary" /> Analytics</h2>
          <p className="text-sm text-slate-500">Andamenti e metriche dettagliate della piattaforma.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="analytics-controls">
          {data.locations?.length > 0 && (
            <div className="flex items-center gap-1.5 px-2 h-9 rounded-full bg-white border border-[#E3F2FD]">
              <MapPin size={16} className="text-primary" />
              <select
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="bg-transparent text-sm outline-none pr-1"
                data-testid="analytics-location-filter"
              >
                <option value="">Tutte le zone</option>
                {data.locations.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          )}
          {(data.service_types?.length > 0 || data.has_unspecified_service) && (
            <div className="flex items-center gap-1.5 px-2 h-9 rounded-full bg-white border border-[#E3F2FD]">
              <Briefcase size={16} className="text-primary" />
              <select
                value={serviceType}
                onChange={(e) => setServiceType(e.target.value)}
                className="bg-transparent text-sm outline-none pr-1"
                data-testid="analytics-service-filter"
              >
                <option value="">Tutte le prestazioni</option>
                {data.service_types.map((s) => <option key={s} value={s}>{s}</option>)}
                {data.has_unspecified_service && <option value="__none__">Non specificata</option>}
              </select>
            </div>
          )}
          <div className="flex items-center gap-1.5 px-2 h-9 rounded-full bg-white border border-[#E3F2FD]" data-testid="analytics-date-range">
            <Calendar size={16} className="text-primary" />
            <input
              type="date"
              value={start}
              max={end || undefined}
              onChange={(e) => setStart(e.target.value)}
              className="bg-transparent text-sm outline-none"
              data-testid="analytics-date-start"
              aria-label="Data inizio"
            />
            <span className="text-slate-400 text-sm">→</span>
            <input
              type="date"
              value={end}
              min={start || undefined}
              onChange={(e) => setEnd(e.target.value)}
              className="bg-transparent text-sm outline-none"
              data-testid="analytics-date-end"
              aria-label="Data fine"
            />
            {(start || end) && (
              <button
                onClick={() => { setStart(''); setEnd(''); }}
                className="ml-1 text-xs font-medium text-primary hover:underline"
                data-testid="analytics-date-reset"
              >
                Azzera
              </button>
            )}
          </div>
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => exportReport('pdf')} data-testid="analytics-export-pdf">
            <DownloadSimple size={15} className="mr-1" /> PDF
          </Button>
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => exportReport('xlsx')} data-testid="analytics-export-xlsx">
            <DownloadSimple size={15} className="mr-1" /> Excel
          </Button>
        </div>
      </div>

      {data.alerts?.length > 0 && (
        <div className="space-y-2" data-testid="analytics-alerts">
          {data.alerts.map((a, i) => (
            <div key={i} className={`flex items-center gap-2 rounded-xl p-3 text-sm border ${a.level === 'warning' ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-sky-50 border-sky-200 text-sky-800'}`} data-testid={`analytics-alert-${i}`}>
              <Warning size={18} className="shrink-0" /> {a.message}
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testid="analytics-kpi-requests" icon={ClipboardText} label="Richieste" value={t.requests} delta={data.deltas.requests} sub={`Questa sett: ${data.deltas.requests.current}`} />
        <Kpi testid="analytics-kpi-bookings" icon={Calendar} label="Prenotazioni" value={t.bookings} delta={data.deltas.bookings} sub={`Conversione ${t.conversion_rate}%`} />
        <Kpi testid="analytics-kpi-revenue" icon={CurrencyEur} label="Fatturato pagato" value={`€${t.paid_revenue}`} delta={data.deltas.revenue} sub={`${t.paid_bookings} prenot. pagate`} />
        <Kpi testid="analytics-kpi-avg" icon={CurrencyEur} label="Valore medio" value={`€${t.avg_booking_value}`} sub={`Valore totale €${t.booking_value_total}`} />
        <Kpi testid="analytics-kpi-users" icon={Users} label="Clienti" value={t.users} />
        <Kpi testid="analytics-kpi-profs" icon={Users} label="Professionisti" value={t.professionals_approved} sub={`${t.professionals_pending} in attesa`} />
        <Kpi testid="analytics-kpi-premium" icon={Crown} label="Premium" value={t.professionals_premium} />
        <Kpi testid="analytics-kpi-conversion" icon={TrendUp} label="Conversione" value={`${t.conversion_rate}%`} sub="Prenotazioni / richieste" />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="rounded-2xl p-5" data-testid="analytics-comparison-card">
          <p className="font-heading font-medium mb-4 flex items-center gap-2"><TrendUp size={18} className="text-primary" /> Confronto periodi</p>
          {['month', 'year'].map((per) => (
            <div key={per} className="mb-3 last:mb-0">
              <p className="text-xs uppercase tracking-wider text-foreground/50 mb-1">{per === 'month' ? 'Mese su mese' : 'Anno su anno'}</p>
              <div className="grid grid-cols-3 gap-2">
                {[['requests', 'Richieste'], ['bookings', 'Prenotazioni'], ['revenue', 'Fatturato €']].map(([k, lbl]) => {
                  const c = data.comparison[per][k];
                  return (
                    <div key={k} className="bg-[#F5FAFF] rounded-xl p-2.5" data-testid={`comparison-${per}-${k}`}>
                      <p className="text-[11px] text-foreground/60">{lbl}</p>
                      <p className="text-lg font-bold text-foreground">{c.current}</p>
                      <Delta d={c} />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </Card>

        <Card className="rounded-2xl p-5" data-testid="analytics-goals-card">
          <p className="font-heading font-medium mb-1 flex items-center gap-2"><Target size={18} className="text-primary" /> Obiettivi mensili</p>
          <p className="text-xs text-foreground/60 mb-4">Imposta i target del mese; se il valore attuale è sotto soglia ricevi un avviso in cima.</p>
          <div className="space-y-3">
            <div>
              <label className="text-sm text-foreground/70">Richieste / mese (attuale: {data.goals.current_requests_month})</label>
              <Input type="number" min="0" value={goals.target_requests_month}
                onChange={(e) => setGoals({ ...goals, target_requests_month: e.target.value })}
                data-testid="goal-requests-input" />
            </div>
            <div>
              <label className="text-sm text-foreground/70">Prenotazioni / mese (attuale: {data.goals.current_bookings_month})</label>
              <Input type="number" min="0" value={goals.target_bookings_month}
                onChange={(e) => setGoals({ ...goals, target_bookings_month: e.target.value })}
                data-testid="goal-bookings-input" />
            </div>
            <Button onClick={saveGoals} disabled={savingGoals} className="rounded-full" data-testid="goal-save-btn">
              {savingGoals ? <CircleNotch size={16} className="animate-spin mr-2" /> : null} Salva obiettivi
            </Button>
          </div>
        </Card>
      </div>

      <Card className="rounded-2xl p-5" data-testid="analytics-trends-chart">
        <p className="font-heading font-medium mb-4">Andamento settimanale — Richieste, Prenotazioni & Fatturato</p>
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={data.series} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E3F2FD" />
            <XAxis dataKey="week_label" tick={{ fontSize: 12, fill: '#5B7A96' }} />
            <YAxis yAxisId="left" tick={{ fontSize: 12, fill: '#5B7A96' }} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12, fill: '#2CA9E1' }} />
            <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E3F2FD' }} />
            <Legend />
            <Bar yAxisId="left" dataKey="requests" name="Richieste" fill="#1565C0" radius={[6, 6, 0, 0]} barSize={14} />
            <Bar yAxisId="left" dataKey="bookings" name="Prenotazioni" fill="#2CA9E1" radius={[6, 6, 0, 0]} barSize={14} />
            <Line yAxisId="right" type="monotone" dataKey="revenue" name="Fatturato €" stroke="#0D3B66" strokeWidth={2.5} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </Card>

      {data.response_time && (
        <Card className="rounded-2xl p-5" data-testid="analytics-response-time-card">
          <div className="flex items-center gap-2 mb-1">
            <Timer size={20} className="text-primary" weight="duotone" />
            <p className="font-heading font-medium">Tempo di risposta dei professionisti</p>
          </div>
          <p className="text-xs text-foreground/60 mb-5">Tempo trascorso dalla segnalazione all'accettazione della richiesta.</p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Tempo medio', value: `${data.response_time.avg_hours}h`, testid: 'rt-avg' },
              { label: 'Tempo mediano', value: `${data.response_time.median_hours}h`, testid: 'rt-median' },
              { label: 'Tasso accettazione', value: `${data.response_time.acceptance_rate}%`, sub: `${data.response_time.accepted_count} su ${data.response_time.notified_count} segnalate`, testid: 'rt-rate' },
              { label: 'Senza risposta >24h', value: data.response_time.pending_over_24h, testid: 'rt-pending' },
            ].map(m => (
              <div key={m.testid} className="border border-border rounded-xl p-4" data-testid={m.testid}>
                <p className="text-xs text-foreground/60 uppercase tracking-wider">{m.label}</p>
                <p className="text-2xl font-heading font-bold mt-1">{m.value}</p>
                {m.sub && <p className="text-[11px] text-foreground/50 mt-0.5">{m.sub}</p>}
              </div>
            ))}
          </div>

          {data.response_time.accepted_count === 0 ? (
            <p className="text-sm text-foreground/60" data-testid="rt-empty">
              Nessuna richiesta ancora accettata: le statistiche appariranno dopo le prime accettazioni.
            </p>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-xs font-medium text-foreground/70 mb-2">Distribuzione dei tempi di accettazione</p>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={data.response_time.buckets}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E3F2FD" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#5B7A96' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#5B7A96' }} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E3F2FD' }} />
                    <Bar dataKey="count" name="Richieste" fill="#2CA9E1" radius={[6, 6, 0, 0]} barSize={38} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div>
                <p className="text-xs font-medium text-foreground/70 mb-2">Professionisti più rapidi</p>
                <div className="space-y-1.5" data-testid="rt-top-responders">
                  {data.response_time.top_responders.map((p, i) => (
                    <div key={p.name} className="flex items-center justify-between border border-border rounded-lg px-3 py-2">
                      <span className="text-sm truncate">{i + 1}. {p.name}</span>
                      <span className="text-sm font-medium text-primary shrink-0">{p.avg_hours}h <span className="text-foreground/50 font-normal">· {p.accepted} acc.</span></span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-foreground/50 mt-3">
                  Più rapido: {data.response_time.fastest_hours}h · Più lento: {data.response_time.slowest_hours}h
                </p>
              </div>
            </div>
          )}
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="rounded-2xl p-5" data-testid="analytics-growth-chart">
          <p className="font-heading font-medium mb-4">Nuovi clienti e professionisti / settimana</p>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={data.series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E3F2FD" />
              <XAxis dataKey="week_label" tick={{ fontSize: 11, fill: '#5B7A96' }} />
              <YAxis tick={{ fontSize: 11, fill: '#5B7A96' }} />
              <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E3F2FD' }} />
              <Legend />
              <Bar dataKey="new_users" name="Clienti" fill="#38BDF8" radius={[6, 6, 0, 0]} />
              <Bar dataKey="new_professionals" name="Professionisti" fill="#1565C0" radius={[6, 6, 0, 0]} />
            </ComposedChart>
          </ResponsiveContainer>
        </Card>

        <Card className="rounded-2xl p-5" data-testid="analytics-request-status-chart">
          <p className="font-heading font-medium mb-4">Richieste per stato</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={toArr(data.requests_by_status)} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E3F2FD" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#5B7A96' }} />
              <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: '#5B7A96' }} />
              <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E3F2FD' }} />
              <Bar dataKey="count" name="Richieste" fill="#1565C0" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="rounded-2xl p-5" data-testid="analytics-services-chart">
          <p className="font-heading font-medium mb-4">Prestazioni più richieste</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.top_services} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E3F2FD" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#5B7A96' }} />
              <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: '#5B7A96' }} />
              <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E3F2FD' }} />
              <Bar dataKey="count" name="Volume" fill="#2CA9E1" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="rounded-2xl p-5" data-testid="analytics-payment-chart">
          <p className="font-heading font-medium mb-4">Stato pagamenti prenotazioni</p>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={toArr(data.payment_status)} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                {toArr(data.payment_status).map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E3F2FD' }} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {data.top_locations?.length > 0 && (
        <Card className="rounded-2xl p-5" data-testid="analytics-locations-chart">
          <p className="font-heading font-medium mb-4">Zone più attive</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.top_locations}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E3F2FD" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#5B7A96' }} />
              <YAxis tick={{ fontSize: 11, fill: '#5B7A96' }} />
              <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E3F2FD' }} />
              <Bar dataKey="count" name="Richieste" fill="#1565C0" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}
    </div>
  );
};

export default AnalyticsPage;
